/* eslint-disable no-restricted-globals */
import { useState, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import API from 'api/api';
import { Heading3, SpacedH1, useBankingOptionStyles } from 'assets/styles';

import { ROUTES, REPORT_POLL_INTERVAL, PLAID_PRODUCTS } from 'constants/constants';

import { CustomButton } from 'common-components/ActionButton/styles';
import BankVerifying from 'pages/Banking/components/BankVerifying';
import ActionButton from 'common-components/ActionButton/ActionButton';
import BackLink from 'common-components/BackLink/BackLink';
import withTransactionPath from 'utils/withTransactionPath';
import PlaidLink from '../components/PlaidLink';
import BankingContext from 'pages/Banking/BankingContext';
import type { TransactionState } from 'reducers/transaction';
import type { getTransactionPath } from 'types/types';
import { getIncomeCopyUpdateForCortland } from 'selectors/launchDarkly';

export interface ProofDocument {
    id: number;
    label: string;
    description: string;
    min_required: number;
    max_required: number;
}

// Move this to redux state in the future
export interface FinancialDocumentsValidations {
    allow_manual_document_upload_plaid: boolean;
    lease_settings: number;
    income_or_asset_type: number;
    proof_documents: ProofDocument[];
    stream_type: number;
    require_all: boolean;
}

// Move this to redux state in the future
export interface ConfigurationState {
    financial_documents_validations?: FinancialDocumentsValidations[];
    plaid_payroll_digital_flow_enabled: boolean;
    plaid_payroll_manual_flow_enabled: boolean;
}

// Configuration typing is coming soon, but for now it's too massive to do in this PR
interface PlaidAddPayrollProps {
    configuration: ConfigurationState;
    history: import('react-router').RouteComponentProps['history'];
    leaseTransaction?: TransactionState;
    getTransactionPath: getTransactionPath;
    incomeCopyUpdateForCortland: boolean;
}

export function PlaidAddPayroll(props: PlaidAddPayrollProps) {
    const context = useContext(BankingContext);
    const classes = useBankingOptionStyles();
    const [stateObj, setStateObj] = useState({
        showPlaidLink: false,
        loadingReport: false,
        plaidProduct: PLAID_PRODUCTS.INCOME_VERIFICATION_WITH_PAYROLL_PAYSTUB,
    });
    const hasManualDocumentUploadPlaidEnabled = useMemo(
        () =>
            props?.configuration?.financial_documents_validations?.some(
                (validation) => validation?.allow_manual_document_upload_plaid
            ),
        [props?.configuration?.financial_documents_validations]
    );
    const hasPlaidPayrollDigitalFlowEnabled = useMemo(
        () => props?.configuration?.plaid_payroll_digital_flow_enabled,
        [props?.configuration?.plaid_payroll_digital_flow_enabled]
    );
    const hasPlaidPayrollManualFlowEnabled = useMemo(
        () => props?.configuration?.plaid_payroll_manual_flow_enabled,
        [props?.configuration?.plaid_payroll_manual_flow_enabled]
    );

    function renderDigitalFlowSection() {
        if (hasPlaidPayrollDigitalFlowEnabled) {
            return (
                <div data-testid="plaid_payroll_digital_flow_section">
                    <Heading3 align="left">Link your payroll provider</Heading3>
                    <span className={classes.pillbox}>INSTANT VERIFICATION</span>
                    <span className={classes.pillbox}>SECURE</span>
                    <br />
                    <p className={classes.body}>
                        Select your payroll provider, sign in, and confirm your payroll information.
                    </p>
                    <ActionButton
                        variant="outlined"
                        onClick={() => {
                            setStateObj({
                                showPlaidLink: true,
                                loadingReport: false,
                                plaidProduct: PLAID_PRODUCTS.INCOME_VERIFICATION_WITH_PAYROLL,
                            });
                        }}
                    >
                        Link Payroll
                    </ActionButton>
                </div>
            );
        }

        return null;
    }

    function renderBulletPoints() {
        if (hasPlaidPayrollManualFlowEnabled) {
            return (
                <>
                    <div data-testid="plaid_payroll_manual_flow_section">
                        <Heading3 align="left">Upload income documents</Heading3>
                        <p className={classes.body}>To verify your income, choose from the following options:</p>
                        <ul className={classes.body}>
                            <li>
                                <strong>1 tax document:</strong> Upload either a W-2 or 1099 form from the last year.{' '}
                                {props.incomeCopyUpdateForCortland &&
                                    `We accept W-2's up until June of the current year.`}
                            </li>
                            <li>
                                <strong>3 paystubs:</strong>{' '}
                                {props?.incomeCopyUpdateForCortland
                                    ? 'Upload last 3 months of paystubs.'
                                    : 'Upload 3 recent paystubs within the past 3 months.'}{' '}
                                Please upload each paystub as a separate document.
                            </li>
                            {hasManualDocumentUploadPlaidEnabled && (
                                <li>
                                    <strong>Other income documents:</strong>{' '}
                                    {props?.incomeCopyUpdateForCortland
                                        ? `Upload other supporting income documents (e.g. offer letter, social security benefit letter, financial aid award letter, veteran income, disability income, etc.)`
                                        : `Upload other supporting income proof (e.g.,
                                    offer letter, vouchers).`}
                                </li>
                            )}
                        </ul>
                    </div>
                </>
            );
        }

        return null;
    }

    const handleGetPlaidPayrollIncome = async (publicToken: string, reportType: string, plaidProduct: string) => {
        try {
            if (props.leaseTransaction?.id) {
                await API.getPlaidIncomeAndAssets(props.leaseTransaction.id, publicToken, reportType, plaidProduct);
                let response = await API.fetchPlaidReports(props.leaseTransaction.id, reportType);
                while (response.status === 202) {
                    await new Promise((resolve) => setTimeout(resolve, REPORT_POLL_INTERVAL));
                    response = await API.fetchPlaidReports(props.leaseTransaction.id, reportType);
                }

                await context.refreshFinancialSources();
                await context.fetchTransaction();
                props.history.push(props?.getTransactionPath(ROUTES.INCOME_VERIFICATION_SUMMARY), null);
            }
        } catch {
            setStateObj({
                showPlaidLink: false,
                loadingReport: false,
                plaidProduct: PLAID_PRODUCTS.INCOME_VERIFICATION_WITH_PAYROLL_PAYSTUB,
            });
        }
    };

    if (stateObj.loadingReport) {
        return <BankVerifying />;
    }

    if (stateObj.showPlaidLink) {
        return (
            <PlaidLink
                leaseTransactionId={props.leaseTransaction?.id}
                plaidProduct={stateObj.plaidProduct}
                updateState={setStateObj}
                handleOnSuccessCallback={handleGetPlaidPayrollIncome}
                onClose={() => {
                    props.history.push(
                        props.getTransactionPath(ROUTES.INCOME_VERIFICATION_SUMMARY, {
                            lease_transaction_id: props.leaseTransaction?.id,
                        }),
                        null
                    );
                }}
            />
        );
    }

    return (
        <>
            <SpacedH1 variant="h1">Add Income</SpacedH1>
            <br />
            <div>
                {renderDigitalFlowSection()}
                {renderBulletPoints()}
                <div>
                    <ActionButton
                        variant="outlined"
                        onClick={() => {
                            setStateObj({
                                showPlaidLink: true,
                                loadingReport: false,
                                plaidProduct: PLAID_PRODUCTS.INCOME_VERIFICATION_WITH_PAYROLL_TAXFORM,
                            });
                        }}
                    >
                        Add Tax Form
                    </ActionButton>
                    <br />
                    <ActionButton
                        variant="outlined"
                        onClick={() => {
                            setStateObj({
                                showPlaidLink: true,
                                loadingReport: false,
                                plaidProduct: PLAID_PRODUCTS.INCOME_VERIFICATION_WITH_PAYROLL_PAYSTUB,
                            });
                        }}
                    >
                        Add Paystubs
                    </ActionButton>
                    <div className={classes.footer}>You’ll be redirected to Plaid, our financial data provider.</div>
                    <br />
                    {hasManualDocumentUploadPlaidEnabled && (
                        <>
                            <Link
                                to={props.getTransactionPath(ROUTES.PLAID_MANUAL_INCOME_ENTRY_ADD_INCOME, {
                                    lease_transaction_id: props.leaseTransaction?.id,
                                })}
                            >
                                <CustomButton variant="outlined" color="primary" fullWidth>
                                    Add Other Income Documents
                                </CustomButton>
                            </Link>
                            <br />
                        </>
                    )}
                    <br />
                    <BackLink
                        to={props.getTransactionPath(ROUTES.INCOME_VERIFICATION_SUMMARY, {
                            lease_transaction_id: props.leaseTransaction?.id,
                        })}
                    />
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (
    state: Readonly<{
        configuration: ConfigurationState;
    }>
) => ({
    configuration: state.configuration,
    incomeCopyUpdateForCortland: getIncomeCopyUpdateForCortland(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withTransactionPath(PlaidAddPayroll));
